import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import SaveIndicator from '@/components/canvas/SaveIndicator';
import { CanvasNumber, StatusBadge, familyOf, minutesLabel } from '@/components/canvas/bits';

// The top of a canvas page: arc, title, the question it answers, how long it takes, and the guide controls.
export default function CanvasHeader({ def, state, typed, allGuides, onToggleGuides, onFillExample, onReset }) {
  const fam = familyOf(def);
  return (
    <header className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className={cn('h-1.5', fam.solid)} />
      <div className="space-y-4 p-5 md:p-6">
        <div className="flex flex-wrap items-start gap-4">
          <CanvasNumber def={def} size="lg" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className={cn('text-xs font-semibold uppercase tracking-wider', fam.ink)}>
              {def.arc} · Day {def.day}, Module {def.module}
            </p>
            <h1 className="font-heading text-2xl font-semibold text-balance md:text-3xl">{def.no === 0 ? 'Case Card' : def.title}</h1>
            <p className="text-muted-foreground">{def.question}</p>
          </div>
          <StatusBadge state={state} />
        </div>
        <p className="max-w-3xl text-sm leading-relaxed">{def.purpose}</p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <ApperIcon name="Clock" size={14} /> About {minutesLabel(def)}
          </span>
          {def.sceptic && (
            <span className="flex items-center gap-1.5">
              <ApperIcon name="MessagesSquare" size={14} /> Sceptic: {def.sceptic}
            </span>
          )}
          <SaveIndicator className="sm:hidden" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant={allGuides ? 'default' : 'outline'}
            size="sm"
            aria-pressed={allGuides}
            onClick={onToggleGuides}
          >
            <ApperIcon name="CircleHelp" size={16} />
            {allGuides ? 'Hide all guides' : 'Show all guides'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                <ApperIcon name="Ellipsis" size={16} />
                More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              <DropdownMenuItem
                disabled={typed || state.confirmed}
                onSelect={onFillExample}
              >
                <ApperIcon name="Sparkles" size={16} />
                <span className="flex flex-col">
                  <span>Fill with the NorthStar example</span>
                  <span className="text-xs text-muted-foreground">Only on an empty canvas</span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={!typed && !state.confirmed} onSelect={onReset}>
                <ApperIcon name="RotateCcw" size={16} />
                Start this canvas again
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
